/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import {
  DateRangePicker,
  DateRange,
  type DateRangeOption,
  SelectWithIcon,
  useLocalization,
} from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";

export type TxFilters = {
  dateRange: DateRange;
  method?: string;
  type?: "sale" | "credit";
  status?: string;
  userId?: number;
};

interface TransactionFiltersProps {
  filters: TxFilters;
  onFiltersChange: (filters: TxFilters) => void;
  /** Called after Apply is tapped on mobile — lets PageHeader close the sheet. */
  onApply?: () => void;
}

export function buildFilterSummary(
  filters: TxFilters,
  t: (key: string) => string,
  selectedUserName?: string,
): string {
  const DATE_RANGE_LABELS: Record<string, string> = {
    today: t("reports.tx.filters.today"),
    yesterday: t("reports.tx.filters.yesterday"),
    this_week: t("reports.tx.filters.this_week"),
    last_week: t("reports.tx.filters.last_week"),
    this_month: t("reports.tx.filters.this_month"),
    last_month: t("reports.tx.filters.last_month"),
    this_quarter: t("reports.tx.filters.this_quarter"),
    last_quarter: t("reports.tx.filters.last_quarter"),
    this_year: t("reports.tx.filters.this_year"),
    last_year: t("reports.tx.filters.last_year"),
    last_12m: t("reports.tx.filters.last_12m"),
  };

  const parts: string[] = [];
  const dateLabel =
    DATE_RANGE_LABELS[filters.dateRange.type] ??
    `${filters.dateRange.startDate} – ${filters.dateRange.endDate}`;
  parts.push(dateLabel);
  if (filters.method)
    parts.push(
      filters.method.charAt(0).toUpperCase() + filters.method.slice(1),
    );
  if (filters.type)
    parts.push(
      filters.type === "credit"
        ? t("reports.tx.filters.refunds_only")
        : t("reports.tx.filters.sales_only"),
    );
  if (filters.status)
    parts.push(
      filters.status.charAt(0).toUpperCase() + filters.status.slice(1),
    );
  if (filters.userId) parts.push(selectedUserName ?? t("kds.filters.staff"));
  return parts.join(" · ");
}

export const TransactionFilters = defineComponent<TransactionFiltersProps>({
  name: "TransactionFilters",
  props: ["filters", "onFiltersChange", "onApply"],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    const isStaff = auth.user.value?.role === "staff";

    const STAFF_DATE_OPTIONS = computed<DateRangeOption[]>(() => [
      { value: "today", label: t("reports.tx.filters.today") },
      { value: "yesterday", label: t("reports.tx.filters.yesterday") },
    ]);

    const STAFF_OPTIONS = computed(() => {
      const staffById = new Map<number, string>();
      const users = [
        ...(auth.user.value ? [auth.user.value] : []),
        ...(auth.user.value?.colleagues ?? []),
      ];

      for (const user of users) {
        if (user.role === "staff" && !user.profile.deleted_at) {
          staffById.set(user.id, user.profile.name);
        }
      }

      return [...staffById].map(([id, name]) => ({
        value: id.toString(),
        label: name,
      }));
    });

    const METHOD_OPTIONS = computed(() => [
      { value: "cash", label: t("reports.tx.filters.method_cash") },
      { value: "card", label: t("reports.tx.filters.method_card") },
      { value: "twint", label: t("reports.tx.filters.method_twint") },
      { value: "manual", label: t("reports.tx.filters.method_manual") },
      {
        value: "table_credit",
        label: t("reports.tx.filters.method_table_credit"),
      },
    ]);

    const TYPE_OPTIONS = computed(() => [
      { value: "sale", label: t("reports.tx.filters.type_sales") },
      { value: "credit", label: t("reports.tx.filters.type_refunds") },
    ]);

    const STATUS_OPTIONS = computed(() => [
      { value: "success", label: t("reports.tx.filters.status_success") },
      { value: "voided", label: t("reports.tx.filters.status_voided") },
      { value: "pending", label: t("reports.tx.filters.status_pending") },
      { value: "authorized", label: t("reports.tx.filters.status_authorized") },
    ]);

    // Pending state — committed only when Apply is pressed (mobile).
    const pending = ref<TxFilters>({ ...props.filters });

    const applySheet = () => {
      props.onFiltersChange({ ...pending.value });
      props.onApply?.();
    };

    const clearSheet = () => {
      pending.value = { dateRange: props.filters.dateRange };
      props.onFiltersChange({ dateRange: props.filters.dateRange });
      props.onApply?.();
    };

    // Count active non-default filters against the *committed* props.filters.
    const activeFilterCount = computed(() => {
      let n = 0;
      if (props.filters.dateRange.type !== "today") n++;
      if (props.filters.method) n++;
      if (props.filters.type) n++;
      if (props.filters.status) n++;
      if (props.filters.userId) n++;
      return n;
    });

    // Count pending changes so the Apply button can show a diff count.
    const pendingFilterCount = computed(() => {
      let n = 0;
      if (pending.value.dateRange.type !== "today") n++;
      if (pending.value.method) n++;
      if (pending.value.type) n++;
      if (pending.value.status) n++;
      if (pending.value.userId) n++;
      return n;
    });

    // Desktop: applies immediately.
    const setDesktop = (patch: Partial<TxFilters>) =>
      props.onFiltersChange({ ...props.filters, ...patch });

    // ── Desktop (inline in PageHeader) ───────────────────────────────────────
    const renderDesktop = () => (
      <>
        <DateRangePicker
          value={props.filters.dateRange}
          options={isStaff ? STAFF_DATE_OPTIONS.value : undefined}
          presetsOnly={isStaff}
          onChange={(dateRange) => setDesktop({ dateRange })}
        />
        <SelectWithIcon
          icon="credit_card"
          value={props.filters.method ?? ""}
          placeholder={t("reports.tx.filters.placeholder_method")}
          options={METHOD_OPTIONS.value}
          class="w-36"
          onChange={(v) => setDesktop({ method: v || undefined })}
        />
        <SelectWithIcon
          icon="swap_horiz"
          value={props.filters.type ?? ""}
          placeholder={t("reports.tx.filters.placeholder_type")}
          options={TYPE_OPTIONS.value}
          class="w-32"
          onChange={(v) =>
            setDesktop({ type: (v as "sale" | "credit") || undefined })
          }
        />
        <SelectWithIcon
          icon="radio_button_checked"
          value={props.filters.status ?? ""}
          placeholder={t("reports.tx.filters.placeholder_status")}
          options={STATUS_OPTIONS.value}
          class="w-36"
          onChange={(v) => setDesktop({ status: v || undefined })}
        />
        <SelectWithIcon
          icon="person"
          value={props.filters.userId?.toString() ?? ""}
          placeholder={t("orders.filter.all_staff")}
          options={STAFF_OPTIONS.value}
          class="w-40"
          onChange={(v) => setDesktop({ userId: v ? Number(v) : undefined })}
        />
        {activeFilterCount.value > 0 && (
          <button
            class="btn btn-secondary hover:text-error"
            title="Clear non-date filters"
            onClick={() =>
              setDesktop({
                method: undefined,
                type: undefined,
                status: undefined,
                userId: undefined,
              })
            }
          >
            <span class="material-symbols-outlined text-base">
              filter_alt_off
            </span>
          </button>
        )}
      </>
    );

    // ── Mobile sheet content (rendered inside PageHeader's BottomSheet) ───────
    const renderMobileSheet = () => (
      <div class="flex flex-col gap-5 p-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
            {t("reports.tx.filters.date_range")}
          </label>
          <DateRangePicker
            value={pending.value.dateRange}
            options={isStaff ? STAFF_DATE_OPTIONS.value : undefined}
            presetsOnly={isStaff}
            onChange={(dateRange) =>
              (pending.value = { ...pending.value, dateRange })
            }
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
            {t("reports.tx.filters.payment_method")}
          </label>
          <SelectWithIcon
            icon="credit_card"
            value={pending.value.method ?? ""}
            placeholder={t("reports.tx.filters.all_methods")}
            options={METHOD_OPTIONS.value}
            class="w-full"
            selectClass="w-full"
            onChange={(v) =>
              (pending.value = { ...pending.value, method: v || undefined })
            }
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
            {t("reports.tx.filters.transaction_type")}
          </label>
          <SelectWithIcon
            icon="swap_horiz"
            value={pending.value.type ?? ""}
            placeholder={t("reports.tx.filters.all_types")}
            options={TYPE_OPTIONS.value}
            class="w-full"
            selectClass="w-full"
            onChange={(v) =>
              (pending.value = {
                ...pending.value,
                type: (v as "sale" | "credit") || undefined,
              })
            }
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
            {t("reports.tx.filters.placeholder_status")}
          </label>
          <SelectWithIcon
            icon="radio_button_checked"
            value={pending.value.status ?? ""}
            placeholder={t("reports.tx.filters.all_statuses")}
            options={STATUS_OPTIONS.value}
            class="w-full"
            selectClass="w-full"
            onChange={(v) =>
              (pending.value = { ...pending.value, status: v || undefined })
            }
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
            {t("kds.filters.staff")}
          </label>
          <SelectWithIcon
            icon="person"
            value={pending.value.userId?.toString() ?? ""}
            placeholder={t("orders.filter.all_staff")}
            options={STAFF_OPTIONS.value}
            class="w-full"
            selectClass="w-full"
            onChange={(v) =>
              (pending.value = {
                ...pending.value,
                userId: v ? Number(v) : undefined,
              })
            }
          />
        </div>
        <div class="flex gap-2 pt-1">
          <button class="btn btn-primary btn-sm flex-1" onClick={applySheet}>
            <span class="material-symbols-outlined text-base">check</span>
            {t("reports.tx.filters.apply_button")}
            {pendingFilterCount.value > 0 && (
              <span class="badge badge-sm badge-primary-content ml-1">
                {pendingFilterCount.value}
              </span>
            )}
          </button>
          {pendingFilterCount.value > 0 && (
            <button
              class="btn btn-ghost btn-sm text-error"
              onClick={clearSheet}
            >
              {t("reports.tx.filters.clear_button")}
            </button>
          )}
        </div>
      </div>
    );

    return () => (
      <>
        {/* PageHeader shows this div on desktop (md:block wrapper), hides on mobile */}
        <div class="hidden md:flex items-end gap-2 flex-wrap">
          {renderDesktop()}
        </div>
        {/* PageHeader shows this in BottomSheet on mobile, CSS hides it on desktop */}
        <div class="md:hidden">{renderMobileSheet()}</div>
      </>
    );
  },
});
